import { ArrowRight, BarChart3 } from "lucide-react";
import { Link } from "react-router";
import { ChangeIndicator } from "~/components/company/financials/change-indicator";
import { Sparkline } from "~/components/company/financials/sparkline";
import { formatCurrency, formatPercent } from "~/lib/format";
import type { CompanyProfile, FinancialSummary } from "~/types/company";
import { CardFooterLink, OverviewCard } from "./card";

type FinancialKey = "revenue" | "netProfit" | "totalAssets" | "equity" | "foreignResources";

function computeYoY(financials: FinancialSummary[], key: FinancialKey): number | null {
	const comparable = financials.filter((financial) => financial[key] != null);
	if (comparable.length < 2) return null;
	const current = comparable[0][key];
	const prior = comparable[1][key];
	if (current == null || prior == null || prior === 0) return null;
	return (current - prior) / Math.abs(prior);
}

function equityRatio(financial: FinancialSummary): number | null {
	if (financial.equity == null || financial.totalAssets == null || financial.totalAssets === 0) {
		return null;
	}
	return financial.equity / financial.totalAssets;
}

function computeRatioYoY(financials: FinancialSummary[]): number | null {
	const ratios = financials.map(equityRatio).filter((ratio): ratio is number => ratio != null);
	if (ratios.length < 2 || ratios[1] === 0) return null;
	return (ratios[0] - ratios[1]) / Math.abs(ratios[1]);
}

function KpiItem({
	label,
	value,
	change,
	sparkData,
}: {
	label: string;
	value: string;
	change?: number | null;
	sparkData?: (number | null)[];
}) {
	const hasSparkline = sparkData != null && sparkData.filter((item) => item != null).length > 1;
	return (
		<div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4">
			<p className="text-xs font-medium text-muted-foreground">{label}</p>
			<p className="text-xl font-mono font-semibold tabular-nums">{value}</p>
			{(change != null || hasSparkline) && (
				<div className="flex items-center justify-between mt-1">
					{change != null ? <ChangeIndicator value={change} /> : <span />}
					{sparkData && hasSparkline && <Sparkline data={sparkData} />}
				</div>
			)}
		</div>
	);
}

/** ② Finanční přehled — latest-year KPIs; empty state routes to /dokumenty. */
export function FinancialCard({ company }: { company: CompanyProfile }) {
	const balanceFinancials = company.financials.filter(
		(financial) => financial.availability?.balanceSheet,
	);
	const incomeFinancials = company.financials.filter(
		(financial) => financial.availability?.profitLoss,
	);
	const latestBalance = balanceFinancials[0] ?? null;
	const latestIncome = incomeFinancials[0] ?? null;

	if (!latestBalance && !latestIncome) {
		return (
			<OverviewCard icon={BarChart3} title="Finanční přehled" hue="teal">
				<div className="flex flex-col items-center gap-3 py-6 text-center">
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
						<BarChart3 className="h-6 w-6 text-muted-foreground" />
					</div>
					<p className="text-sm font-medium">Žádné finanční údaje</p>
					<Link
						to="dokumenty"
						prefetch="intent"
						className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
					>
						Účetní závěrky čekají na zpracování — zobrazit dokumenty
						<ArrowRight className="h-3.5 w-3.5" />
					</Link>
				</div>
			</OverviewCard>
		);
	}

	const balanceKpis = latestBalance
		? [
				{
					label: "Aktiva",
					value: latestBalance.totalAssets,
					change: computeYoY(balanceFinancials, "totalAssets"),
					sparkData: balanceFinancials.map((financial) => financial.totalAssets).reverse(),
					format: formatCurrency,
				},
				{
					label: "Vlastní kapitál",
					value: latestBalance.equity,
					change: computeYoY(balanceFinancials, "equity"),
					sparkData: balanceFinancials.map((financial) => financial.equity).reverse(),
					format: formatCurrency,
				},
				{
					label: "Cizí zdroje",
					value: latestBalance.foreignResources,
					change: computeYoY(balanceFinancials, "foreignResources"),
					sparkData: balanceFinancials.map((financial) => financial.foreignResources).reverse(),
					format: formatCurrency,
				},
				{
					label: "Míra vlastního kapitálu",
					value: equityRatio(latestBalance),
					change: computeRatioYoY(balanceFinancials),
					sparkData: balanceFinancials.map(equityRatio).reverse(),
					format: formatPercent,
				},
			].filter((kpi) => kpi.value != null)
		: [];

	const incomeKpis = latestIncome
		? [
				{
					label: "Tržby",
					value: latestIncome.revenue,
					change: computeYoY(incomeFinancials, "revenue"),
					sparkData: incomeFinancials.map((financial) => financial.revenue).reverse(),
				},
				{
					label: "Čistý zisk",
					value: latestIncome.netProfit,
					change: computeYoY(incomeFinancials, "netProfit"),
					sparkData: incomeFinancials.map((financial) => financial.netProfit).reverse(),
				},
			].filter((kpi) => kpi.value != null)
		: [];

	return (
		<OverviewCard hue="teal" icon={BarChart3} title="Finanční přehled">
			{latestBalance && balanceKpis.length > 0 && (
				<section aria-labelledby="overview-balance-heading">
					<h3 id="overview-balance-heading" className="mb-3 text-sm font-semibold">
						Rozvaha {latestBalance.year}
					</h3>
					<div className="grid grid-cols-2 gap-4">
						{balanceKpis.map((kpi) => (
							<KpiItem
								key={kpi.label}
								label={kpi.label}
								value={kpi.format(kpi.value)}
								change={kpi.change}
								sparkData={kpi.sparkData}
							/>
						))}
					</div>
				</section>
			)}

			{latestIncome && incomeKpis.length > 0 && (
				<section
					aria-labelledby="overview-income-heading"
					className={latestBalance ? "mt-5 border-t border-border pt-5" : undefined}
				>
					<h3 id="overview-income-heading" className="mb-3 text-sm font-semibold">
						Poslední zveřejněná výsledovka {latestIncome.year}
					</h3>
					<div className="grid grid-cols-2 gap-4">
						{incomeKpis.map((kpi) => (
							<KpiItem
								key={kpi.label}
								label={kpi.label}
								value={formatCurrency(kpi.value)}
								change={kpi.change}
								sparkData={kpi.sparkData}
							/>
						))}
					</div>
				</section>
			)}
			<CardFooterLink to="finance">Kompletní finance</CardFooterLink>
		</OverviewCard>
	);
}
